import React, { useEffect, useState } from "react";
import api from "../../utils/api";
import { Link, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MagnifyingGlass, DownloadSimple, User, Clock, ChartBar, CheckCircle, ArrowLeft, FunnelSimple, Users, FileX } from "@phosphor-icons/react";
import { ChevronRight, Eye, AlertCircle, RefreshCw } from "lucide-react";

const Attempted = () => {
  const { id } = useParams();
  const [attemptedPapers, setAttemptedPapers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [noOfAttempts, setNoOfAttempts] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [dataLoading, setDataLoading] = useState(false);

  const TableLoader = ({ rows = 5, columns = 8 }) => (
    <tbody>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={rowIndex}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td key={colIndex} style={{ padding: "16px", textAlign: colIndex === 0 ? "center" : "left" }}>
              <div style={{
                height: "20px",
                backgroundColor: "#f0f0f0",
                borderRadius: "4px",
                animation: "pulse 1.5s infinite"
              }}></div>
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );

  async function fetchList() {
    try {
      setDataLoading(true);
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "getStudentsListOfPaperAttempted";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);
      fd.append("paperId", id);

      const response = await api.post("examPaper/exam", fd);
      // console.log("ss", response)

      if (response.data.success) {
        const formatted = response.data.data.flatMap((item) => {
          if (item.examRecords.length > 0) {
            return item.examRecords.map((record) => {
              let subjectSummary = [];
              let totalMarks = "-";

              if (
                record.questionAndAnswers &&
                record.questionAndAnswers !== "Cheating Detected"
              ) {
                try {
                  const qna = JSON.parse(record.questionAndAnswers);
                  const lastItem = qna[qna.length - 1];

                  if (lastItem?.summary?.sections) {
                    subjectSummary = lastItem.summary.sections.flatMap((sec) =>
                      sec.subjects.map((s) => ({
                        sectionName: sec.sectionName?.trim() || "Unknown Section",
                        subjectName: s.subjectName?.trim() || "Unknown Subject",
                        total: s.total,
                        correct: s.marks,
                        wrong: s.wrong,
                        unanswered: s.unanswered,
                        marks: s.marks,
                      }))
                    );

                    totalMarks = lastItem.summary.totalMarks || "-";
                  }
                } catch (e) {
                  console.warn("Error parsing questionAndAnswers", e);
                }
              }

              return {
                Student_ID: item.studentInfo.studentId,
                Student_Name: item.studentInfo.firstName,
                Father_Name: item.studentInfo.father_name,
                Roll_Number: item.studentInfo.roll,
                Paper_ID: record.paperId,
                Exam_Record_ID: record.id,
                Total_Questions: record.totalQuestions,
                Total_Answered: record.totalAnsweredQuestions,
                Correct_Answers: record.correctAnswers,
                Wrong_Answers: record.wrongAnswers,
                Exam_Status: record.examStatus,
                Subject_Summary: subjectSummary,
                Total_Marks: totalMarks,
                Start_Time: record.startTime,
                End_Time: record.endTime,
                Extra: record.extraChances
              };
            });
          } else {
            return [
              {
                Student_ID: item.studentInfo.studentId,
                Student_Name: item.studentInfo.firstName,
                Father_Name: item.studentInfo.father_name,
                Roll_Number: item.studentInfo.roll,
                Paper_ID: "-",
                Total_Questions: "-",
                Total_Answered: "-",
                Correct_Answers: "-",
                Wrong_Answers: "-",
                Exam_Status: "Not Attempted",
                Subject_Summary: [],
              },
            ];
          }
        });

        setAttemptedPapers(formatted);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Error fetching data");
    } finally {
      setDataLoading(false);
      setLoading(false);
    }
  }

  const openExtraChanceModal = (student) => {
    setSelectedStudent(student);
    setNoOfAttempts("");
    setShowModal(true);
  };

  async function handleChance() {
    if (!noOfAttempts) {
      toast.error("Please enter number of attempts");
      return;
    }

    try {
      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "requestReattemptForStudent";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);
      fd.append("paperId", id);
      fd.append("studentId", selectedStudent.Student_ID);
      fd.append("noOfAttempts", noOfAttempts);

      const response = await api.post("examPaper/exam", fd);

      if (response.data.success) {
        toast.success("Extra attempt granted successfully!");
        setShowModal(false);
        fetchList();
      } else {
        toast.error("Failed: " + response.data.message);
      }
    } catch (error) {
      console.error("Error requesting extra chance:", error);
      toast.error("Something went wrong!");
    }
  }

  useEffect(() => {
    fetchList();
  }, []);

  const handleExport = () => {
    if (attemptedPapers.length === 0) {
      toast.error("No data available to export");
      return;
    }

    const sectionList = [];
    attemptedPapers.forEach((p) => {
      p.Subject_Summary.forEach((s) => {
        if (s.sectionName && !sectionList.includes(s.sectionName)) {
          sectionList.push(s.sectionName);
        }
      });
    });

    const getSectionScore = (paper, sectionName) => {
      const sectionSubjects = paper.Subject_Summary.filter(
        (s) => s.sectionName === sectionName
      );

      if (sectionSubjects.length === 0) return "-";

      return sectionSubjects.reduce(
        (sum, s) => sum + (Number(s.correct) || 0),
        0
      );
    };

    const headerRow = [
      "S.No",
      "Student Name",
      "Father Name",
      "Roll No.",
      ...sectionList,
      "Total",
    ];

    const dataRows = attemptedPapers.map((p, idx) => [
      idx + 1,
      p.Student_Name,
      p.Father_Name || "-",
      p.Roll_Number,
      ...sectionList.map((section) => getSectionScore(p, section)),
      p.Total_Marks || "-",
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
    worksheet["!cols"] = headerRow.map(() => ({ wch: 18 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attempted Papers");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(data, `attempted_papers_section_wise_${id}.xlsx`);
  };

  const sectionList = [];
  attemptedPapers.forEach((p) => {
    p.Subject_Summary.forEach((s) => {
      if (s.sectionName && !sectionList.includes(s.sectionName)) {
        sectionList.push(s.sectionName);
      }
    });
  });

  function formatDateTime(dateTimeStr) {
    if (!dateTimeStr) return "-";
    const date = new Date(dateTimeStr.replace(" ", "T"));

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  }

  const filteredPapers = attemptedPapers.filter((paper) => {
    if (!searchTerm.trim()) return true;

    const term = searchTerm.trim().toLowerCase();
    const studentName = String(paper.Student_Name || "").toLowerCase();
    const fatherName = String(paper.Father_Name || "").toLowerCase();
    const rollNo = String(paper.Roll_Number || "").toLowerCase();

    return (
      studentName.includes(term) ||
      fatherName.includes(term) ||
      rollNo.includes(term)
    );
  });

  const getSectionScore = (paper, sectionName) => {
    const sectionSubjects = paper.Subject_Summary.filter(
      (s) => s.sectionName === sectionName
    );

    if (sectionSubjects.length === 0) return "-";

    return sectionSubjects.reduce(
      (sum, s) => sum + (Number(s.correct) || 0),
      0
    );
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f8fafc",
      padding: "24px",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
    }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header Section */}
      <div style={{ marginBottom: "32px" }}>


        {/* Search and Filter Card */}
        <div style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "24px",

          boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
        }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "20px"
          }}>
            <h3 style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#334155",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}>
              <FunnelSimple size={20} />
              Filter Students
            </h3>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "nowrap",
                whiteSpace: "nowrap"
              }}
            >

              <div
                style={{
                  position: "relative",
                  width: "320px",
                  minWidth: "260px"
                }}
              >

                <div style={{
                  position: "absolute",
                  left: "16px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94a3b8",
                  zIndex: 1
                }}>
                  <MagnifyingGlass size={20} />
                </div>
                <input
                  type="text"
                  placeholder="Search by Student Name, Roll No, or Father Name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    padding: "12px 20px 12px 48px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    fontSize: "14px",
                    width: "100%",
                    outline: "none",
                    transition: "all 0.2s",
                    background: "#fff",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                  }}
                />
              </div>
              <button
                onClick={handleExport}
                disabled={attemptedPapers.length === 0}
                style={{
                  background: attemptedPapers.length === 0
                    ? "#e2e8f0"
                    : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  color: attemptedPapers.length === 0 ? "#94a3b8" : "#fff",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "10px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: attemptedPapers.length === 0 ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.3s",
                  boxShadow: attemptedPapers.length === 0 ? "none" : "0 2px 8px rgba(16, 185, 129, 0.3)"
                }}
                onMouseEnter={(e) => {
                  if (attemptedPapers.length > 0) {
                    e.target.style.transform = "translateY(-2px)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (attemptedPapers.length > 0) {
                    e.target.style.transform = "translateY(0)";
                  }
                }}
              >
                <DownloadSimple size={18} weight="bold" />
                Export to Excel
              </button>
            </div>
          </div>


        </div>
      </div>

      {/* Results Summary */}
      {attemptedPapers.length > 0 && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "16px",
          marginBottom: "24px"
        }}>
          <div style={{
            background: "#fff",
            borderRadius: "12px",
            padding: "20px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
          }}>
            <div style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: "#eff6ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#3b82f6"
            }}>
              <Users size={24} />
            </div>
            <div>
              <div style={{
                fontSize: "14px",
                color: "#64748b",
                fontWeight: "500"
              }}>
                Total Students
              </div>
              <div style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "#1e293b"
              }}>
                {new Set(attemptedPapers.map(p => p.Student_ID)).size}
              </div>
            </div>
          </div>

          <div style={{
            background: "#fff",
            borderRadius: "12px",
            padding: "20px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
          }}>
            <div style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: "#f0f9ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#0ea5e9"
            }}>
              <CheckCircle size={24} />
            </div>
            <div>
              <div style={{
                fontSize: "14px",
                color: "#64748b",
                fontWeight: "500"
              }}>
                Attempted
              </div>
              <div style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "#1e293b"
              }}>
                {attemptedPapers.filter(p => p.Exam_Status !== "Not Attempted").length}
              </div>
            </div>
          </div>

          <div style={{
            background: "#fff",
            borderRadius: "12px",
            padding: "20px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
          }}>
            <div style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: "#fef2f2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ef4444"
            }}>
              <FileX size={24} />
            </div>
            <div>
              <div style={{
                fontSize: "14px",
                color: "#64748b",
                fontWeight: "500"
              }}>
                Not Attempted
              </div>
              <div style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "#1e293b"
              }}>
                {attemptedPapers.filter(p => p.Exam_Status === "Not Attempted").length}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Table Section */}
      <div style={{
        background: "#fff",
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        marginTop: "16px"
      }}>
        <div style={{
          padding: "20px 24px",
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px"
        }}>
          <div>
            <h3 style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#1e293b",
              margin: 0
            }}>
              Student Attempts List
            </h3>
            <p style={{
              fontSize: "14px",
              color: "#64748b",
              margin: "4px 0 0 0"
            }}>
              Showing {filteredPapers.length} of {attemptedPapers.length} records
            </p>
          </div>

          {filteredPapers.length > 0 && (
            <div style={{
              fontSize: "13px",
              color: "#475569",
              background: "#f8fafc",
              padding: "8px 16px",
              borderRadius: "8px",
              fontWeight: "500"
            }}>
              Paper ID: {id}
            </div>
          )}
        </div>

        <div
          style={{
            overflowX: "auto",
            position: "relative",
            paddingBottom: "6px"
          }}
        >

          <table style={{
            width: "100%",
            borderCollapse: "collapse",
            minWidth: "800px"
          }}>
            <thead style={{
              background: "#f8fafc",
              borderBottom: "2px solid #e5e7eb"
            }}>
              <tr>
                <th style={{
                  padding: "16px",
                  textAlign: "center",
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#475569",
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  width: "80px"
                }}>
                  #
                </th>
                <th style={{
                  padding: "16px",
                  textAlign: "left",
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#475569",
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  width: "220px"
                }}>
                  Student Details
                </th>
                <th style={{
                  padding: "16px",
                  textAlign: "center",
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#475569",
                  textTransform: "uppercase",
                  letterSpacing: "0.4px"
                }}>
                  Roll No.
                </th>

                {/* Section Columns */}
                {sectionList.map((section, idx) => (
                  <th key={idx} style={{
                    padding: "16px",
                    textAlign: "center",

                    fontWeight: "700",
                    color: "#475569",
                    textTransform: "uppercase",
                    letterSpacing: "0.4px",
                    minWidth: "80px",
                    fontSize: "12px"

                  }}>
                    {section}
                  </th>
                ))}

                <th style={{
                  padding: "16px",
                  textAlign: "center",
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#475569",
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  width: "100px"
                }}>
                  Total
                </th>
                <th style={{
                  padding: "16px",
                  textAlign: "center",
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#475569",
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  width: "140px"
                }}>
                  Status
                </th>
                <th style={{
                  padding: "16px",
                  textAlign: "center",
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#475569",
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  width: "120px"
                }}>
                  Actions
                </th>
              </tr>
            </thead>

            {dataLoading ? (
              <TableLoader rows={6} columns={6 + sectionList.length} />
            ) : (
              <tbody>
                {filteredPapers.length === 0 ? (
                  <tr>
                    <td colSpan={8 + sectionList.length} style={{ padding: "60px 20px", textAlign: "center" }}>
                      <div style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "16px",
                        color: "#94a3b8"
                      }}>
                        <div style={{
                          fontSize: "48px",
                          background: "#f1f5f9",
                          width: "80px",
                          height: "80px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}>
                          📊
                        </div>
                        <div style={{ fontSize: "18px", fontWeight: "600", color: "#475569" }}>
                          No attempts found
                        </div>
                        <div style={{ fontSize: "14px", maxWidth: "400px" }}>
                          {searchTerm ? "No students match your search criteria" : "No students have attempted this paper yet"}
                        </div>
                        {!searchTerm && (
                          <button
                            onClick={fetchList}
                            style={{
                              background: "#3E80F9",
                              color: "#fff",
                              border: "none",
                              padding: "10px 24px",
                              borderRadius: "8px",
                              fontSize: "14px",
                              fontWeight: "500",
                              cursor: "pointer",
                              marginTop: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px"
                            }}
                          >
                            <RefreshCw size={16} />
                            Refresh Data
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredPapers.map((paper, index) => (
                    <tr key={index} style={{
                      borderBottom: "1px solid #f1f5f9",
                      transition: "background 0.2s"
                    }}>
                      <td style={{
                        padding: "12px 10px",
                        textAlign: "center",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#475569"
                      }}>
                        <div style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "8px",
                          background: "#f1f5f9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto",
                          fontWeight: "600"
                        }}>
                          {index + 1}
                        </div>
                      </td>

                      <td style={{ padding: "12px 10px" }}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                            minWidth: "200px"
                          }}
                        >
                          <div
                            style={{
                              fontSize: "15px",
                              fontWeight: "600",
                              color: "#1e293b",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              flexWrap: "wrap"
                            }}
                          >
                            {/* Extra Attempt Badge */}
                            {(paper.isExtraAttempt === 1 || Number(paper.Extra) > 0) && (
                              <span
                                style={{
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  padding: "2px 8px",
                                  borderRadius: "999px",
                                  background: "#fde68a",
                                  color: "#92400e",
                                  border: "1px solid #facc15",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  whiteSpace: "nowrap"
                                }}
                                title={`Extra Attempts: ${paper.Extra}`}
                              >
                                Extra Attempt
                              </span>
                            )}

                            <span>{paper.Student_Name}</span>
                          </div>

                          <div style={{
                            fontSize: "13px",
                            color: "#64748b"
                          }}>
                            {paper.Father_Name || "No father name"}
                          </div>

                          {paper.Start_Time && (
                            <div style={{
                              fontSize: "12px",
                              color: "#3b82f6",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              marginTop: "4px"
                            }}>
                              <Clock size={12} />
                              Start: {formatDateTime(paper.Start_Time)}
                            </div>
                          )}

                          {paper.End_Time && (
                            <div style={{
                              fontSize: "12px",
                              color: "#10b981",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px"
                            }}>
                              <Clock size={12} />
                              End: {formatDateTime(paper.End_Time)}
                            </div>
                          )}
                        </div>
                      </td>

                      <td style={{
                        padding: "12px 10px",
                        textAlign: "center",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#334155"
                      }}>
                        {paper.Roll_Number}
                      </td>

                      {/* Section Scores */}
                      {sectionList.map((section) => (
                        <td key={section} style={{
                          padding: "12px 10px",
                          textAlign: "center",
                          fontSize: "15px",
                          fontWeight: "600",
                          color: getSectionScore(paper, section) === "-" ? "#94a3b8" : "#059669"
                        }}>
                          {getSectionScore(paper, section)}
                        </td>
                      ))}

                      <td style={{
                        padding: "12px 10px"
                        ,
                        textAlign: "center",
                        fontSize: "16px",
                        fontWeight: "700",
                        color: paper.Total_Marks === "-" ? "#94a3b8" : "#3b82f6"
                      }}>
                        {paper.Total_Marks || "-"}
                      </td>

                      <td style={{
                        padding: "12px 10px"
                        , textAlign: "center"
                      }}>

                        <div style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "8px"
                        }}>
                          {paper.Exam_Status?.trim() && (
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "500",
                                padding: "6px 12px",
                                borderRadius: "20px",
                                background:
                                  paper.Exam_Status === "Not Attempted"
                                    ? "#fef2f2"
                                    : paper.Exam_Status?.toLowerCase() === "exited"
                                      ? "#fef3c7"
                                      : "#dcfce7",
                                color:
                                  paper.Exam_Status === "Not Attempted"
                                    ? "#dc2626"
                                    : paper.Exam_Status?.toLowerCase() === "exited"
                                      ? "#d97706"
                                      : "#059669",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px"
                              }}
                            >
                              {paper.Exam_Status === "Not Attempted" ? (
                                <AlertCircle size={12} />
                              ) : (
                                <CheckCircle size={12} />
                              )}
                              {paper.Exam_Status}
                            </div>
                          )}


                          {(paper.Exam_Status?.toLowerCase() === "exited" ||
                            paper.Exam_Status?.toLowerCase() === "extra" ||
                            !!paper.Start_Time) && (
                              <button
                                onClick={() => openExtraChanceModal(paper)}
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  padding: "4px 12px",
                                  borderRadius: "6px",
                                  background: "#3b82f6",
                                  color: "#fff",
                                  border: "none",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px"
                                }}
                                onMouseEnter={(e) => e.target.style.background = "#2563eb"}
                                onMouseLeave={(e) => e.target.style.background = "#3b82f6"}
                              >
                                Extra Chance
                              </button>
                            )}
                        </div>
                      </td>

                      <td style={{
                        padding: "12px 10px"
                        , textAlign: "center"
                      }}>
                        {paper.Paper_ID !== "-" ? (
                          <Link
                            to={`/view-result/${paper.Paper_ID}/${paper.Student_ID}/${paper.Exam_Record_ID}`}
                            style={{
                              padding: "8px 16px",
                              borderRadius: "8px",
                              background: "#fef3c7",
                              color: "#d97706",
                              textDecoration: "none",
                              fontSize: "13px",
                              fontWeight: "500",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              transition: "all 0.2s"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#fde68a";
                              e.currentTarget.style.transform = "translateY(-2px)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "#fef3c7";
                              e.currentTarget.style.transform = "translateY(0)";
                            }}
                          >
                            <Eye size={14} />
                            View
                          </Link>
                        ) : (
                          <span style={{
                            fontSize: "13px",
                            color: "#94a3b8",
                            fontStyle: "italic"
                          }}>
                            Not available
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            )}
          </table>
        </div>
      </div>

      {/* Extra Chance Modal */}
      {showModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          padding: "20px"
        }}>
          <div style={{
            background: "#fff",
            borderRadius: "16px",
            width: "100%",
            maxWidth: "480px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)"
          }}>
            <div style={{
              padding: "24px 28px",
              borderBottom: "1px solid #e5e7eb",
              background: "linear-gradient(135deg, #3E80F9 0%, #2d5bff 100%)",
              color: "#fff"
            }}>
              <h3 style={{ fontSize: "18px", fontWeight: "700", margin: 0 }}>
                Grant Extra Chance
              </h3>
              <p style={{ fontSize: "14px", opacity: 0.9, marginTop: "4px" }}>
                Provide additional attempts for the student
              </p>
            </div>

            <div style={{ padding: "28px" }}>
              <div style={{ marginBottom: "20px" }}>
                <div style={{
                  fontSize: "14px",
                  color: "#64748b",
                  marginBottom: "8px",
                  fontWeight: "500"
                }}>
                  Student Information
                </div>
                <div style={{
                  padding: "16px",
                  background: "#f8fafc",
                  borderRadius: "10px",
                  border: "1px solid #e5e7eb"
                }}>
                  <div style={{ fontSize: "15px", fontWeight: "600", color: "#1e293b" }}>
                    {selectedStudent?.Student_Name}
                  </div>
                  <div style={{ fontSize: "13px", color: "#64748b", marginTop: "4px" }}>
                    Roll No: {selectedStudent?.Roll_Number} • ID: {selectedStudent?.Student_ID}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <label style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#334155",
                  marginBottom: "8px"
                }}>
                  Number of Additional Attempts
                </label>
                <input
                  type="number"
                  value={noOfAttempts}
                  onChange={(e) => setNoOfAttempts(e.target.value)}
                  placeholder="Enter number of attempts"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    fontSize: "14px",
                    outline: "none",
                    transition: "all 0.2s",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#3E80F9";
                    e.target.style.boxShadow = "0 0 0 3px rgba(62, 128, 249, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.boxShadow = "0 1px 2px rgba(0,0,0,0.05)";
                  }}
                />
              </div>

              <div style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                marginTop: "8px"
              }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: "12px 24px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                    color: "#475569",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.2s"
                  }}
                  onMouseEnter={(e) => e.target.style.background = "#f8fafc"}
                  onMouseLeave={(e) => e.target.style.background = "#fff"}
                >
                  Cancel
                </button>
                <button
                  onClick={handleChance}
                  style={{
                    padding: "12px 24px",
                    borderRadius: "10px",
                    border: "none",
                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    color: "#fff",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.3s",
                    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)"
                  }}
                  onMouseEnter={(e) => e.target.style.transform = "translateY(-2px)"}
                  onMouseLeave={(e) => e.target.style.transform = "translateY(0)"}
                >
                  Grant Extra Chance
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          
          *:focus {
            outline: 2px solid #3E80F9;
            outline-offset: 2px;
          }
          
          button:hover:not(:disabled) {
            transform: translateY(-2px);
          }
          
          input:hover, select:hover {
            border-color: #94a3b8 !important;
          }
          
          input:focus, select:focus {
            border-color: #3E80F9 !important;
            box-shadow: 0 0 0 3px rgba(62, 128, 249, 0.1) !important;
          }
          
          a {
            text-decoration: none;
          }
        `}
      </style>
    </div>
  );
};

export default Attempted;